import { useCallback, useEffect, useState } from 'react';
import type { Claim } from '@/core/types';
import { getCertByBib, getRandomCerts, getUserCert } from '../services/certs';

interface UseCertsOptions {
  raceAlias: string;
  uid?: string | null;
}

interface UseCertsReturn {
  certs: Claim[];
  loading: boolean;
  error: string | null;
  myCert: Claim | null;
  shuffle: () => Promise<void>;
  searchBib: (bib: string) => Promise<{ found: boolean; cert?: Claim; error?: string }>;
  highlightedId: string | null;
  clearHighlight: () => void;
  searchedCert: Claim | null;
  clearSearchedCert: () => void;
}

export function useCerts(options: UseCertsOptions): UseCertsReturn {
  const { raceAlias, uid } = options;

  const [certs, setCerts] = useState<Claim[]>([]);
  const [myCert, setMyCert] = useState<Claim | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [searchedCert, setSearchedCert] = useState<Claim | null>(null);

  // データ取得
  const fetchCerts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 認証済みの場合は自分の完走証を先に取得
      let userCert: Claim | null = null;
      if (uid) {
        userCert = await getUserCert(raceAlias, uid);
      }
      setMyCert(userCert); // uid有無に関わらず常に更新（ログアウト時にクリアするため）

      // ランダムな完走証を取得（自分のは除外）
      const randomCerts = await getRandomCerts(raceAlias, userCert?.id);
      setCerts(randomCerts);
    } catch (e) {
      console.error('Failed to fetch certs:', e);
      setError('完走証の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [raceAlias, uid]);

  // 初回読み込み
  useEffect(() => {
    fetchCerts();
  }, [fetchCerts]);

  // ゼッケン検索
  const searchBib = useCallback(
    async (bib: string): Promise<{ found: boolean; cert?: Claim; error?: string }> => {
      try {
        const cert = await getCertByBib(raceAlias, bib);
        if (cert) {
          setSearchedCert(cert);
          setHighlightedId(cert.id);
          return { found: true, cert };
        }
        setSearchedCert(null);
        setHighlightedId(null);
        return { found: false };
      } catch (e) {
        console.error('Failed to search bib:', e);
        setSearchedCert(null);
        setHighlightedId(null);
        return { found: false, error: '検索中にエラーが発生しました。もう一度お試しください。' };
      }
    },
    [raceAlias]
  );

  // 検索結果クリア
  const clearSearchedCert = useCallback(() => {
    setSearchedCert(null);
    setHighlightedId(null);
  }, []);

  // ハイライト解除
  const clearHighlight = useCallback(() => {
    setHighlightedId(null);
  }, []);

  // シャッフル（新しいランダムセットを取得）
  const shuffle = useCallback(async () => {
    setSearchedCert(null);
    setHighlightedId(null);
    setLoading(true);
    setError(null);

    try {
      const randomCerts = await getRandomCerts(raceAlias, myCert?.id);
      setCerts(randomCerts);
    } catch (e) {
      console.error('Failed to shuffle certs:', e);
      setError('完走証の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [raceAlias, myCert?.id]);

  return {
    certs,
    loading,
    error,
    myCert,
    shuffle,
    searchBib,
    highlightedId,
    clearHighlight,
    searchedCert,
    clearSearchedCert,
  };
}
